import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { useNavigate } from "react-router-dom";

const C = {
    bg: "#ffffff", surface: "#f7f7f7", border: "#e0e0e0",
    text: "#000000", muted: "#555555", dim: "#888888", accent: "#52658F",
    peach: "#ECDBC9",
};
const SYNE = "'Fraunces', serif";
const MONO = "'Plus Jakarta Sans', sans-serif";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

export default function Login() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isSignUp, setIsSignUp] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        if (!document.querySelector('link[href*="Fraunces"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = FONT_LINK;
            document.head.appendChild(link);
        }
    }, []);

    async function handleSubmit() {
        setLoading(true);
        setError(null);
        setMessage(null);

        if (!email || !password) {
            setError("Please fill in all fields.");
            setLoading(false);
            return;
        }

        try {
            if (isSignUp) {
                const { error } = await supabase.auth.signUp({ email, password });
                if (error) throw error;
                setMessage("Account created! Please check your email to confirm, then sign in.");
            } else {
                const { error } = await supabase.auth.signInWithPassword({ email, password });
                if (error) throw error;
                navigate("/app");
            }
        } catch (err) {
            setError(err.message);
        }

        setLoading(false);
    }

    const inputStyle = {
        background: "#ffffff",
        border: "1.5px solid " + C.border,
        borderRadius: 8,
        padding: "0.75rem 1rem",
        color: C.text,
        fontFamily: MONO,
        fontSize: "0.9rem",
        width: "100%",
        outline: "none",
        boxSizing: "border-box",
        marginBottom: "1rem",
    };

    return (
        <div style={{
            background: C.bg, minHeight: "100vh",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: MONO, padding: "2rem",
        }}>
            {/* Back to home — top left */}
            <button onClick={() => navigate("/")}
                style={{
                    position: "fixed", top: "1.25rem", left: "1.5rem",
                    background: "transparent", border: "none",
                    color: C.dim, fontSize: "0.8rem", cursor: "pointer",
                    fontFamily: MONO, display: "flex", alignItems: "center", gap: "0.35rem",
                }}>
                ← Back to home
            </button>

            <div style={{
                background: C.surface, border: "1px solid " + C.border,
                borderRadius: 16, padding: "2.5rem",
                width: "100%", maxWidth: 400,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}>
                {/* Badge */}
                <div style={{
                    display: "inline-flex", alignItems: "center", gap: "0.5rem",
                    background: C.peach, border: "1px solid #d8c4a8",
                    color: "#5a3a1a", fontSize: "0.72rem", fontWeight: 600,
                    letterSpacing: "0.05em", padding: "0.3rem 0.85rem",
                    borderRadius: 100, marginBottom: "1.5rem",
                }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent, display: "inline-block" }} />
                    Data Gets Balanced
                </div>

                <h1 style={{ fontFamily: SYNE, fontWeight: 800, fontSize: "1.6rem", color: C.text, marginBottom: "0.4rem", letterSpacing: "-0.02em" }}>
                    {isSignUp ? "Create account" : "Welcome back"}
                </h1>
                <p style={{ color: C.muted, fontSize: "0.85rem", marginBottom: "2rem", lineHeight: 1.6 }}>
                    {isSignUp ? "Start fixing your data discrepancies." : "Sign in to your DGB account."}
                </p>

                <input
                    type="email"
                    placeholder="email@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSubmit()}
                    style={inputStyle}
                    onFocus={e => e.target.style.border = "1.5px solid " + C.accent}
                    onBlur={e => e.target.style.border = "1.5px solid " + C.border}
                />
                <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSubmit()}
                    style={inputStyle}
                    onFocus={e => e.target.style.border = "1.5px solid " + C.accent}
                    onBlur={e => e.target.style.border = "1.5px solid " + C.border}
                />

                {error && (
                    <div style={{ color: "#dc2626", fontSize: "0.8rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <span>⚠</span> {error}
                    </div>
                )}

                {message && (
                    <div style={{ color: "#059669", fontSize: "0.8rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <span>✓</span> {message}
                    </div>
                )}

                <button
                    onClick={handleSubmit}
                    disabled={loading}
                    style={{
                        width: "100%",
                        background: loading ? C.border : C.accent,
                        color: loading ? C.dim : "white",
                        border: "none", padding: "0.85rem", borderRadius: 8,
                        fontFamily: SYNE, fontWeight: 700, fontSize: "1rem",
                        cursor: loading ? "not-allowed" : "pointer", marginBottom: "1rem",
                    }}>
                    {loading ? "Please wait..." : isSignUp ? "Create Account" : "Sign In"}
                </button>

                <button
                    onClick={() => { setIsSignUp(!isSignUp); setError(null); setMessage(null); }}
                    style={{
                        width: "100%", background: "transparent", color: C.muted,
                        border: "none", fontSize: "0.85rem", cursor: "pointer", fontFamily: MONO,
                    }}>
                    {isSignUp ? "Already have an account? Sign in" : "Don't have an account? Sign up"}
                </button>
            </div>
        </div>
    );
}
